import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useTask,
  useTasks,
  useUpdateTask,
  TaskState,
  TaskWithRelations,
} from "@/hooks/useTasks";
import { TaskColumn } from "@/components/tasks/TaskColumn";
import { TaskDialog } from "@/components/tasks/TaskDialog";
import { PageHeader } from "@/components/PageHeader";

export default function TasksPage() {
  const { data: tasks, isLoading } = useTasks();
  const updateTask = useUpdateTask();
  const [searchParams, setSearchParams] = useSearchParams();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<TaskWithRelations | null>(
    null,
  );
  const [defaultAssigneeId, setDefaultAssigneeId] = useState<string | null>(
    null,
  );
  const [defaultState, setDefaultState] = useState<TaskState>("backlog");
  const [draggedTaskId, setDraggedTaskId] = useState<string | null>(null);
  const selectedTaskId = searchParams.get("task");
  const { data: selectedTask, isLoading: selectedTaskLoading } = useTask(
    selectedTaskId ?? undefined,
  );

  useEffect(() => {
    if (!selectedTaskId || selectedTaskLoading || !selectedTask) return;

    setEditingTask(selectedTask);
    setDefaultAssigneeId(null);
    setDefaultState("backlog");
    setDialogOpen(true);
  }, [selectedTaskId, selectedTaskLoading, selectedTask]);

  // Group tasks by person, then by state
  const { tasksByPerson, personList } = useMemo(() => {
    if (!tasks) return { tasksByPerson: {}, personList: [] };

    const byPerson: Record<string, Record<TaskState, TaskWithRelations[]>> = {};
    const personMap: Record<string, { id: string; name: string }> = {};

    // Initialize unassigned
    byPerson["unassigned"] = {
      backlog: [],
      todo: [],
      in_progress: [],
      done: [],
    };
    personMap["unassigned"] = { id: "unassigned", name: "Unassigned" };

    tasks.forEach((task) => {
      const state = task.state as TaskState;

      if (task.assignees.length === 0) {
        // Unassigned task
        if (!byPerson["unassigned"][state].find((t) => t.id === task.id)) {
          byPerson["unassigned"][state].push(task);
        }
      } else {
        // Add task to each assignee's column
        task.assignees.forEach((assignee) => {
          const personId = assignee.person.id;
          const personName =
            `${assignee.person.first_name} ${assignee.person.last_name}`.trim();

          if (!byPerson[personId]) {
            byPerson[personId] = {
              backlog: [],
              todo: [],
              in_progress: [],
              done: [],
            };
            personMap[personId] = { id: personId, name: personName };
          }

          if (!byPerson[personId][state].find((t) => t.id === task.id)) {
            byPerson[personId][state].push(task);
          }
        });
      }
    });

    // Sort persons: unassigned first, then alphabetically
    const sortedPersons = Object.values(personMap).sort((a, b) => {
      if (a.id === "unassigned") return -1;
      if (b.id === "unassigned") return 1;
      return a.name.localeCompare(b.name);
    });

    return { tasksByPerson: byPerson, personList: sortedPersons };
  }, [tasks]);

  const handleDragStart = (taskId: string) => {
    setDraggedTaskId(taskId);
  };

  const handleDrop = (newState: TaskState) => {
    if (draggedTaskId) {
      updateTask.mutate({ id: draggedTaskId, state: newState });
      setDraggedTaskId(null);
    }
  };

  const handleEditTask = (task: TaskWithRelations) => {
    setEditingTask(task);
    setDefaultAssigneeId(null);
    setDialogOpen(true);
  };

  const handleCreateTask = () => {
    setEditingTask(null);
    setDefaultAssigneeId(null);
    setDefaultState("backlog");
    setDialogOpen(true);
  };

  const handleCreateTaskForPerson = (
    personId: string,
    state: TaskState = "backlog",
  ) => {
    setEditingTask(null);
    setDefaultAssigneeId(personId);
    setDefaultState(state);
    setDialogOpen(true);
  };

  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <PageHeader title="Tasks" />
        <div className="flex gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-96 w-72" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 h-full flex flex-col">
      <div className="flex items-center justify-between">
        <PageHeader title="Tasks" />
        <Button offlineEdit onClick={handleCreateTask}>
          <Plus className="h-4 w-4 mr-2" />
          New Task
        </Button>
      </div>

      <div className="flex-1 overflow-x-auto border rounded-lg">
        <div className="flex h-full min-w-max">
          {personList.map((person, index) => (
            <TaskColumn
              key={person.id}
              personId={person.id}
              personName={person.name}
              tasksByState={
                tasksByPerson[person.id] || {
                  backlog: [],
                  todo: [],
                  in_progress: [],
                  done: [],
                }
              }
              onDragStart={handleDragStart}
              onDrop={handleDrop}
              onEditTask={handleEditTask}
              onCreateTask={handleCreateTaskForPerson}
              isDragging={draggedTaskId !== null}
              isLast={index === personList.length - 1}
            />
          ))}

          {personList.length === 0 && (
            <div className="flex-1 flex items-center justify-center text-muted-foreground py-12">
              No tasks yet. Create one to get started.
            </div>
          )}
        </div>
      </div>

      <TaskDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open && selectedTaskId) {
            const next = new URLSearchParams(searchParams);
            next.delete("task");
            setSearchParams(next, { replace: true });
          }
        }}
        task={editingTask}
        defaultAssigneeId={defaultAssigneeId}
        defaultState={defaultState}
      />
    </div>
  );
}
